import React, { useState, useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { TextField, IconButton } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../../commonDesign/SydSelect'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import { dataCatalog } from '../../../../../../../api/coreData'

const TYPE_OPTIONS = [
  { label: 'Membership (group hierarchy)', value: 'membership' },
  { label: 'Expression (DQL predicate)', value: 'expression' }
]

/**
 * Single scope predicate editor form, used inside a modal.
 * Supports two types: "membership" (group hierarchy traversal, default)
 * and "expression" (raw DQL WHERE fragment).
 *
 * Layout: two-column — left has identity fields, right has type-specific config.
 */
function ScopePredicateEditor ({ predicate, columns, sourceQuery, onSave, onCancel }) {
  const [type, setType] = useState(predicate?.type || 'membership')
  const [code, setCode] = useState(predicate?.code || '')
  const [scopeType, setScopeType] = useState(predicate?.scopeType || '')
  const [description, setDescription] = useState(predicate?.description || '')

  const stepIdCounter = useRef(0)
  const [pathSteps, setPathSteps] = useState(() => {
    const next = (value = null) => ({ id: stepIdCounter.current++, value })
    return predicate?.path?.length >= 2
      ? predicate.path.map(v => next(v))
      : [next(), next()]
  })
  const [column, setColumn] = useState(predicate?.column || '')

  const [expression, setExpression] = useState(predicate?.expression || '')
  const [fromParameter, setFromParameter] = useState(predicate?.fromParameter || 'scopeGroupIds')

  const { data: groupTypesData } = dataCatalog.useListCatalogGroupTypes()

  const groupTypeOptions = useMemo(() => {
    return (groupTypesData?.data || []).map(gt => ({
      value: gt.groupTypeId,
      label: `${gt.longName} (${gt.groupTypeId})`
    }))
  }, [groupTypesData])

  const columnOptions = useMemo(() => {
    return (columns || []).map(c => ({
      value: c.name,
      label: c.name
    }))
  }, [columns])

  const isNew = !predicate?.code
  const codeValid = code.trim() && /^[a-z0-9-]+$/.test(code.trim())

  const isValid = useMemo(() => {
    if (!codeValid || !scopeType.trim()) return false
    if (type === 'membership') {
      const validSteps = pathSteps.filter(s => s.value != null)
      return validSteps.length >= 2 && column.trim().length > 0
    }
    return expression.trim().length > 0 && fromParameter.trim().length > 0
  }, [type, codeValid, scopeType, pathSteps, column, expression, fromParameter])

  const handlePathStepChange = useCallback((index, value) => {
    setPathSteps(prev => prev.map((s, i) =>
      i === index ? { ...s, value: value ? Number(value) : null } : s
    ))
  }, [])

  const handleAddStep = useCallback(() => {
    setPathSteps(prev => {
      const last = prev.at(-1)
      const newStep = { id: stepIdCounter.current++, value: null }
      return [...prev.slice(0, -1), newStep, last]
    })
  }, [])

  const handleRemoveStep = useCallback((index) => {
    setPathSteps(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleSave = useCallback(() => {
    if (!isValid) return

    const base = {
      code: code.trim(),
      scopeType: scopeType.trim(),
      description: description.trim() || null,
      type
    }

    if (type === 'membership') {
      onSave({ ...base, path: pathSteps.map(s => s.value).filter(v => v != null), column: column.trim() })
    } else {
      onSave({ ...base, expression: expression.trim(), fromParameter: fromParameter.trim() })
    }
  }, [type, code, scopeType, description, pathSteps, column, expression, fromParameter, isValid, onSave])

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', padding: '0 4px' }}>
        {/* Left column: identity */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SydLabel required label='Scope Name' helperText='The scope name this predicate responds to (must match the scope picker, e.g., Advisor, Office)'>
            <SydInput
              value={scopeType}
              onChange={(e) => setScopeType(e?.target?.value ?? e)}
              placeholder='Advisor'
            />
          </SydLabel>

          <SydLabel required label='Scope Predicate Code' helperText='Lowercase letters, numbers, and hyphens (e.g., scope-advisor)'>
            <SydInput
              value={code}
              onChange={(e) => setCode(e?.target?.value ?? e)}
              placeholder='scope-advisor'
              disabled={!isNew}
            />
          </SydLabel>

          <SydLabel label='Description'>
            <SydInput
              value={description}
              onChange={(e) => setDescription(e?.target?.value ?? e)}
              placeholder="Scope to selected advisor's clients"
            />
          </SydLabel>
        </div>

        {/* Right column: type + type-specific config */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SydLabel required label='Type' helperText='Membership traverses the group hierarchy; Expression uses a raw DQL WHERE fragment'>
            <SydSelect
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={TYPE_OPTIONS}
            />
          </SydLabel>

          {type === 'membership' && (
            <>
              <SydLabel required label='Hierarchy Path' helperText='Level types defining the traversal from scope entity to dataset entity'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {pathSteps.map((step, index) => (
                    <div key={step.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-color-subtle)', minWidth: '40px' }}>
                        {index === 0 ? 'From' : index === pathSteps.length - 1 ? 'To' : `Step ${index}`}
                      </span>
                      <div style={{ flex: 1 }}>
                        <SydSelect
                          value={step.value ?? ''}
                          onChange={(e) => handlePathStepChange(index, e.target.value)}
                          options={groupTypeOptions}
                          placeholder='Select level type'
                        />
                      </div>
                      {pathSteps.length > 2 && index > 0 && index < pathSteps.length - 1 && (
                        <IconButton size='small' onClick={() => handleRemoveStep(index)} title='Remove step' aria-label='Remove step'>
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      )}
                    </div>
                  ))}
                  <SydButton size='xs' variant='outline' icon='add' onClick={handleAddStep}>
                    Add Intermediate Step
                  </SydButton>
                </div>
              </SydLabel>

              <SydLabel required label='Column' helperText='The column in the dataset that the scope filters against'>
                <SydSelect
                  value={column}
                  onChange={(e) => setColumn(e.target.value)}
                  options={columnOptions}
                  placeholder='Select column'
                />
              </SydLabel>
            </>
          )}

          {type === 'expression' && (
            <>
              {sourceQuery && (
                <SydLabel label='Purpose Source Query' helperText='Read-only reference for authoring the expression'>
                  <div style={{
                    fontSize: '0.75rem',
                    lineHeight: '1.5',
                    backgroundColor: 'var(--color-surface-alt, #f5f5f5)',
                    border: '1px solid var(--border-color, #e0e0e0)',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    maxHeight: '120px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: 'var(--text-color-subtle)'
                  }}
                  >
                    {sourceQuery}
                  </div>
                </SydLabel>
              )}

              <SydLabel required label='Expression' helperText='SQL WHERE fragment applied when this scope is active'>
                <TextField
                  value={expression}
                  onChange={(e) => setExpression(e.target.value)}
                  size='small'
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  placeholder='h.advisorId IN (:scopeGroupIds)'
                  InputProps={{
                    style: { fontFamily: 'var(--font-mono, monospace)', fontSize: '0.875rem' }
                  }}
                />
              </SydLabel>

              <SydLabel required label='From Parameter' helperText='Parameter name that receives the selected group IDs'>
                <SydInput
                  value={fromParameter}
                  onChange={(e) => setFromParameter(e?.target?.value ?? e)}
                  placeholder='scopeGroupIds'
                />
              </SydLabel>
            </>
          )}
        </div>
      </div>

      <SydModalActions style={{ marginTop: '24px' }}>
        <SydButton variant='ghost' size='md' onClick={onCancel}>
          Cancel
        </SydButton>
        <SydButton
          disabled={!isValid}
          variant='primary'
          size='md'
          onClick={handleSave}
        >
          {isNew ? 'Add Scope Predicate' : 'Save Changes'}
        </SydButton>
      </SydModalActions>
    </div>
  )
}

ScopePredicateEditor.propTypes = {
  predicate: PropTypes.shape({
    code: PropTypes.string,
    scopeType: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.oneOf(['expression', 'membership']),
    expression: PropTypes.string,
    fromParameter: PropTypes.string,
    path: PropTypes.arrayOf(PropTypes.number),
    column: PropTypes.string
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired
  })),
  sourceQuery: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

ScopePredicateEditor.defaultProps = {
  predicate: null,
  columns: [],
  sourceQuery: ''
}

export default ScopePredicateEditor
